import { useState } from 'react';
import { useDraggable } from '@dnd-kit/react';
import { useTagFormAvailableTags, TagForm, TagFormAvailableTag } from '../../../hooks/useTagForm';

interface Props {
  project: any;
  tagForm: TagForm;
  onAddComponent: (type: 'radio' | 'checkbox' | 'text') => void;
  onAddTag: () => void;
  onAddTagElement: (entry: TagFormAvailableTag) => void;
}

type ComponentType = 'radio' | 'checkbox' | 'text';

const COMPONENT_ICONS: Record<ComponentType, string> = {
  radio: 'far fa-fw fa-dot-circle fa-lg',
  checkbox: 'far fa-fw fa-check-square fa-lg',
  text: 'fas fa-fw fa-tag fa-lg',
};

function DraggableComponentButton({ type, onAdd }: { type: ComponentType; onAdd: () => void }) {
  const { ref, isDragging } = useDraggable({
    id: `palette-component-${type}`,
    data: { palette: true, componentType: type },
  });
  return (
    <button
      ref={ref}
      className="btn btn-default btn-block"
      style={{ textAlign: 'left', marginBottom: 4, opacity: isDragging ? 0.4 : 1, cursor: 'grab' }}
      onClick={onAdd}
    >
      <i className={COMPONENT_ICONS[type]} aria-hidden="true" /> {type.charAt(0).toUpperCase() + type.slice(1)}
    </button>
  );
}

function DraggableTagButton({ entry, onAdd }: { entry: TagFormAvailableTag; onAdd: () => void }) {
  const { ref, isDragging } = useDraggable({
    id: `palette-${entry.tag.id}`,
    data: { palette: true, entry },
  });
  return (
    <button
      ref={ref}
      className="btn btn-default btn-block"
      style={{ textAlign: 'left', marginBottom: 4, opacity: isDragging ? 0.4 : 1, cursor: 'grab' }}
      onClick={onAdd}
    >
      <i className={entry.type === 'radio' ? 'far fa-fw fa-dot-circle fa-lg' : 'far fa-fw fa-check-square fa-lg'} aria-hidden="true" />
      {' '}{entry.tag.name}
    </button>
  );
}

export function LeftPanel({ project, tagForm, onAddComponent, onAddTag, onAddTagElement }: Props) {
  const usedTagIds = tagForm.elements
    .filter(e => e.tagId)
    .flatMap(e => [e.tagId!, e.inverseTag?.id].filter(Boolean) as string[]);

  const [tagsPage, setTagsPage] = useState(1);
  const { data: availableTags } = useTagFormAvailableTags(project.id, usedTagIds, tagsPage);

  return (
    <div>
      <h5>Form Components</h5>
      <DraggableComponentButton type="radio" onAdd={() => onAddComponent('radio')} />
      <DraggableComponentButton type="checkbox" onAdd={() => onAddComponent('checkbox')} />
      <DraggableComponentButton type="text" onAdd={() => onAddComponent('text')} />

      <br />
      <br />

      <h5>Available Tags</h5>
      {(availableTags?.totalPages ?? 0) > 1 && (
        <div className="text-center" style={{ marginTop: 8 }}>
          <button className="btn btn-xs btn-default" disabled={tagsPage <= 1} onClick={() => setTagsPage(p => p - 1)}>‹</button>
          <span style={{ margin: '0 8px' }}>{tagsPage}/{availableTags?.totalPages}</span>
          <button className="btn btn-xs btn-default" disabled={tagsPage >= (availableTags?.totalPages ?? 1)} onClick={() => setTagsPage(p => p + 1)}>›</button>
        </div>
      )}
      <button className="btn btn-primary btn-block" style={{ marginBottom: 5 }} onClick={onAddTag}>
        Add Tag <i className="fas fa-plus-circle" aria-hidden="true" />
      </button>
      {availableTags?.elements.map(entry => (
        <DraggableTagButton
          key={entry.tag.id}
          entry={entry}
          onAdd={() => onAddTagElement(entry)}
        />
      ))}
    </div>
  );
}
